import {AccountDetailsType, StandaloneAccountFormData} from "../models/StandaloneAccount";
import React, {ChangeEvent, useEffect, useRef, useState} from "react";
import {LegalEntityFormData, OwnershipDetailsFormData} from "../models/Ownership";
import {RequiredFieldsBanner} from "../../components";
import AccountDetails from "./AccountDetails";
import TaxDetails from "../TaxDetails/TaxDetails";
import Ownership from "../Ownership";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {selectProfile} from "../../ClientManagement/ClientProfile/activeProfileSlice";
import {assetsApiClient} from "../AssetsApiClient";
import DataEntryHeader from "../../components/DataEntry/DataEntryHeader";
import {setActiveFormAsset} from "../clientAssetsSlice";
import deepEquals from "fast-deep-equal";
import {TaxDetailsType, TaxStatusOptions} from "../models/TaxDetails";
import {mapStandaloneAccountToAccountDetails, mapStandaloneAccountToTaxDetails} from "./mappers";
import {extractOwnershipDetailsFormData, mapToOwnershipWriteModel} from "../Ownership/mappers";
import {AccountSummary} from "../AccountsCommon/AccountSummary";
import {isOwnershipPercentageNotEqual100} from "../Ownership/validation";
import {calculateInEstateFormValue} from "../AssetSummary/common/AssetSummaryCalculator";
import {hasSomeInEstateOwnershipInForm} from "../Ownership/helpers";
import {AssetClassifications} from "../models/AssetClassifications";
import {MemberGroup} from "../../ClientManagement/models/InvestorGroupType";
import useProfileEditableState from "../../hooks/useProfileEditableState";
import {HistoryBlockModal} from "../../components/HistoryBlockModal/HistoryBlockModal";

type StandaloneAccountFormParams = {
    formatTitle: (accountName: string) => string,
    handleSave: (accountDetails: AccountDetailsType, taxDetails: TaxDetailsType, ownershipDetails: OwnershipDetailsFormData) => Promise<void>,
    handleCancel: (isFormChanged: boolean) => void,
    onClickEditHoldings: (accountDetails: AccountDetailsType, taxDetails: TaxDetailsType, ownershipDetails: OwnershipDetailsFormData) => Promise<void>,
    initialStandaloneAccount: StandaloneAccountFormData,
    classifications: AssetClassifications,
    memberGroup: MemberGroup,
    legalEntities: LegalEntityFormData[],
    updateLegalEntities: (legalEntities: LegalEntityFormData[]) => void,
    liabilityPaidByPortfolioForTaxableAccounts: boolean,
    liabilityPaidByPortfolioForDeferredAccounts: boolean
};

const defaultDoesPermitBeneficiary = false;
const defaultIsEntityCapitalGains = true;

export function StandaloneAccountForm(props: StandaloneAccountFormParams) {
    const dispatch = useAppDispatch();
    const mounted = useRef(false);
    const initialUnrealizedCapitalGainsTax = props.initialStandaloneAccount.holdings?.unrealizedCapitalGainsTax === undefined
        ? null : props.initialStandaloneAccount.holdings?.unrealizedCapitalGainsTax;
    const initialInvestablePresentValue = props.initialStandaloneAccount.holdings?.totalInvestablePresentValue === undefined
        ? null : props.initialStandaloneAccount.holdings?.totalInvestablePresentValue;

    const {isProfileWithProposalsOrArchived} = useProfileEditableState();
    const profile = useAppSelector(selectProfile);
    const [accountDetails, updateAccountDetails] = useState(mapStandaloneAccountToAccountDetails(props.initialStandaloneAccount));
    const [taxDetails, updateTaxDetails] = useState(mapStandaloneAccountToTaxDetails(props.initialStandaloneAccount));
    const [ownershipDetailsFormData, updateOwnershipDetailsFormData] = useState(extractOwnershipDetailsFormData(props.initialStandaloneAccount));
    const [unrealizedCapitalGainsTax, updateUnrealizedCapitalGainsTax] = useState(initialUnrealizedCapitalGainsTax);
    const [deferredTaxLiability, updateDeferredTaxLiability] = useState(props.initialStandaloneAccount.holdings?.deferredTaxLiability);
    const [isSaveButtonDisabled, updateSaveButtonDisabled] = useState(isProfileWithProposalsOrArchived);
    const [isRequiredFieldsBannerShown, setRequiredFieldsBannerShown] = useState(false);
    const [isOwnershipPercentageErrorBannerShown, setOwnershipPercentageErrorBannerShown] = useState(false);
    const [totalInvestablePresentValue, updateTotalInvestablePresentValue] = useState(initialInvestablePresentValue);
    const [showNavigationModal, setShowNavigationModal] = useState(true);

    useEffect(() => {
        const totalValue = props.initialStandaloneAccount.holdings?.totalMarketValue ?? 0;
        dispatch(setActiveFormAsset({
            assetType: 'standaloneAccount',
            id: props.initialStandaloneAccount.id,
            inEstateValue: calculateInEstateFormValue(totalValue, ownershipDetailsFormData.memberOwnerships),
            description: accountDetails.name,
            hasInEstateOwnership: hasSomeInEstateOwnershipInForm(mapToOwnershipWriteModel(ownershipDetailsFormData).memberOwnerships),
        }));
        return () => {
            dispatch(setActiveFormAsset(null));
        };
    }, [accountDetails.name, ownershipDetailsFormData]);

    useEffect(() => {
        setRequiredFieldsBannerShown(isRequiredFieldsBannerShown && isAnyRequiredFieldEmpty());
    }, [accountDetails, ownershipDetailsFormData.legalEntityOwnerships, taxDetails]);

    useEffect(() => {
        setOwnershipPercentageErrorBannerShown(isOwnershipPercentageErrorBannerShown &&
            isOwnershipPercentageNotEqual100(ownershipDetailsFormData));
    }, [ownershipDetailsFormData.legalEntityOwnerships, ownershipDetailsFormData.memberOwnerships]);

    useEffect(() => {
        mounted.current = true;
        if (accountDetails.taxStatus === "Deferred") {
            const updatedTaxDetails: TaxDetailsType = {
                isEntityCapitalGains: null,
                isLiabilityPaidByPortfolio: props.liabilityPaidByPortfolioForDeferredAccounts,
            };
            updateTaxDetails(updatedTaxDetails);
            handleDeferredTaxLiabilityChange()
                .catch(reason => console.log(reason));
            updateUnrealizedCapitalGainsTax(null);
        } else if (accountDetails.taxStatus === "Taxable" && taxDetails.isEntityCapitalGains !== null) {
            handleUnrealizedCapitalGainsTaxChange(taxDetails.isEntityCapitalGains)
                .catch(reason => console.log(reason));
            updateDeferredTaxLiability(null);
        } else {
            updateUnrealizedCapitalGainsTax(null);
            updateDeferredTaxLiability(null);
        }
        return () => {
            mounted.current = false;
        }
    }, [accountDetails.taxStatus]);

    //TODO: What is the impact of this function in this page and summary
    const getInvestablePresentValue = async (liabilityPaidByPortfolio: boolean) => {
        if(liabilityPaidByPortfolio && props.initialStandaloneAccount.id){
            let totalInvestablePresentValueResponse = await assetsApiClient.getInvestablePresentValue(profile.id,
                props.initialStandaloneAccount.id, accountDetails.taxStatus);
            if (mounted.current) updateTotalInvestablePresentValue(totalInvestablePresentValueResponse);
        } else {
            updateTotalInvestablePresentValue(null);
        }
    }

    const handleUnrealizedCapitalGainsTaxChange = async (isEntityCapitalGains: boolean) => {
        if (props.initialStandaloneAccount.id) {
            let unrealizedCapitalGainsTaxResponse = await assetsApiClient.getUnrealizedCapitalGainsTax(profile.id, props.initialStandaloneAccount.id, isEntityCapitalGains);
            if (mounted.current) updateUnrealizedCapitalGainsTax(unrealizedCapitalGainsTaxResponse);
        }
    }

    const handleDeferredTaxLiabilityChange = async () => {
        if (props.initialStandaloneAccount.id) {
            let deferredTaxLiabilityResponse = await assetsApiClient.getDeferredTaxLiability(profile.id, props.initialStandaloneAccount.id);
            if (mounted.current) updateDeferredTaxLiability(deferredTaxLiabilityResponse);
        }
    }

    const isAnyRequiredFieldEmpty = () => {
        const accountNameIsBlank = !accountDetails.name.trim();
        const asOfDateIsBlank = !accountDetails.asOfDate?.trim();
        const isLiabilityQuestionVisibleForTaxable = accountDetails.taxStatus === "Taxable" && !!taxDetails.isEntityCapitalGains;
        const isLiabilityQuestionVisibleForDeferred = accountDetails.taxStatus === "Deferred";
        const isLiabilityQuestionVisible = isLiabilityQuestionVisibleForTaxable || isLiabilityQuestionVisibleForDeferred;
        const isLiabilityQuestionBlank = taxDetails.isLiabilityPaidByPortfolio === null;

        const isOwnershipDataMissing = ownershipDetailsFormData.legalEntityOwnerships.some((ownership) => {
            return !ownership.name.trim() || !ownership.type;
        });
        return accountNameIsBlank || asOfDateIsBlank || (isLiabilityQuestionBlank && isLiabilityQuestionVisible) || isOwnershipDataMissing;
    };

    const resetAccountAndTaxDetailsToDefaults = (selectedTaxStatus: TaxStatusOptions) => {
        const updatedAccountDetails: AccountDetailsType = {
            ...accountDetails,
            taxStatus: selectedTaxStatus,
            doesPermitBeneficiary: null,
        };
        const updatedTaxDetails: TaxDetailsType = {
            isEntityCapitalGains: null,
            isLiabilityPaidByPortfolio: null,
        };

        switch (selectedTaxStatus) {
            case "Taxable":
                updatedTaxDetails.isEntityCapitalGains = defaultIsEntityCapitalGains;
                updatedTaxDetails.isLiabilityPaidByPortfolio = props.liabilityPaidByPortfolioForTaxableAccounts
                break;
            case "Deferred":
                updatedAccountDetails.doesPermitBeneficiary = defaultDoesPermitBeneficiary;
                updatedTaxDetails.isLiabilityPaidByPortfolio = props.liabilityPaidByPortfolioForDeferredAccounts
                break;
            case "Exempt":
                updatedAccountDetails.doesPermitBeneficiary = defaultDoesPermitBeneficiary;
                break;
            default:
                break;
        }
        return {updatedAccountDetails, updatedTaxDetails};
    };

    const handleTaxStatusChange = (selectedTaxStatus: TaxStatusOptions) => {
        const {updatedAccountDetails, updatedTaxDetails} = resetAccountAndTaxDetailsToDefaults(selectedTaxStatus);
        updateTaxDetails(updatedTaxDetails);
        updateAccountDetails(updatedAccountDetails);
    }

    const handleUpdateAccountDetails = (e: ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        updateAccountDetails({
            ...accountDetails,
            [name]: value
        });
    };

    const isFormChanged = () => {
        const updated: StandaloneAccountFormData = {
            ...accountDetails,
            ...taxDetails,
            ...ownershipDetailsFormData,
        };
        const initial: StandaloneAccountFormData = {
            ...mapStandaloneAccountToAccountDetails(props.initialStandaloneAccount),
            ...mapStandaloneAccountToTaxDetails(props.initialStandaloneAccount),
            ...extractOwnershipDetailsFormData(props.initialStandaloneAccount),
        };
        return !deepEquals(initial, updated);
    };

    const onCancelClick = () => {
        setShowNavigationModal(false)
        props.handleCancel(isFormChanged());
    }

    const handleSaveButtonClick = async () => {
        const {isValid} = validateForm();
        if (isValid) {
            updateSaveButtonDisabled(true);
            setShowNavigationModal(false);
            await props.handleSave(accountDetails, taxDetails, ownershipDetailsFormData);
            return true
        }
        return false
    };

    const handleEditHoldingsClick = async () => {
        setShowNavigationModal(false)
        const {isValid} = validateForm();
        if (isValid) {
            await props.onClickEditHoldings(accountDetails, taxDetails, ownershipDetailsFormData);
        }
    };

    const validateForm = () => {
        const isRequiredFieldEmpty = isAnyRequiredFieldEmpty();
        if (isRequiredFieldEmpty) {
            setRequiredFieldsBannerShown(true);
        }

        const isOwnershipPercentageInvalid = isOwnershipPercentageNotEqual100(ownershipDetailsFormData);
        if (isOwnershipPercentageInvalid) {
            setOwnershipPercentageErrorBannerShown(true);
        }

        return {
            isValid: !isRequiredFieldEmpty && !isOwnershipPercentageInvalid
        };
    };

    const totalAssetValue = props.initialStandaloneAccount.holdings ? props.initialStandaloneAccount.holdings.totalMarketValue : null;

    return <div className="layout-data-entry-form">
        <HistoryBlockModal
            when={isFormChanged() && showNavigationModal}
            itemType={'page'}
            onSave={handleSaveButtonClick}
        />
        <DataEntryHeader
            title={props.formatTitle(accountDetails.name)}
            primaryButtonText="Save"
            onPrimaryButtonClick={handleSaveButtonClick}
            disablePrimaryButton={isSaveButtonDisabled}
            secondaryButtonText="Cancel"
            onSecondaryButtonClick={onCancelClick}
        />
        <RequiredFieldsBanner showAlert={isRequiredFieldsBannerShown} itemType="standalone account"/>
        <div className="standalone-account__form layout-data-entry-form">
            <article>
                <AccountDetails
                    accountDetails={accountDetails}
                    handleUpdateAccountDetails={handleUpdateAccountDetails}
                    updateAccountDetails={updateAccountDetails}
                    handleTaxStatusChange={handleTaxStatusChange}
                    isRequiredFieldsBannerShown={isRequiredFieldsBannerShown}
                    isProfileWithProposalsOrArchived={isProfileWithProposalsOrArchived}
                />
                {(accountDetails.taxStatus === "Taxable" || accountDetails.taxStatus === "Deferred") &&
                    <TaxDetails taxDetails={taxDetails}
                                updateTaxDetails={updateTaxDetails}
                                taxStatus={accountDetails.taxStatus}
                                isRequiredFieldsBannerShown={isRequiredFieldsBannerShown}
                                unrealizedCapitalGainsTax={unrealizedCapitalGainsTax}
                                deferredTaxLiability={deferredTaxLiability}
                                totalInvestablePresentValue={totalInvestablePresentValue}
                                handleUnrealizedCapitalGainsTaxChange={handleUnrealizedCapitalGainsTaxChange}
                                handleLiabilityPaidByPortfolioChange={getInvestablePresentValue}
                                isProfileWithProposalsOrArchived={isProfileWithProposalsOrArchived}
                    />
                }
                <Ownership
                    onFormDataChange={updateOwnershipDetailsFormData}
                    formData={ownershipDetailsFormData}
                    totalAssetValue={totalAssetValue}
                    isOwnershipPercentageErrorBannerShown={isOwnershipPercentageErrorBannerShown}
                    memberGroup={props.memberGroup}
                    legalEntities={props.legalEntities}
                    updateLegalEntities={props.updateLegalEntities}
                    isProfileWithProposalsOrArchived={isProfileWithProposalsOrArchived}
                />
            </article>
            <AccountSummary
                assetType={'standaloneAccount'}
                holdings={props.initialStandaloneAccount.holdings}
                unrealizedCapitalGainsTax={unrealizedCapitalGainsTax}
                deferredTaxLiability={deferredTaxLiability}
                onClick={handleEditHoldingsClick}
                classifications={props.classifications}
            />
        </div>
    </div>;
}